import { useMemo, useRef } from 'react';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from 'apollo-link-error';

import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from '@apollo/client/link/ws';
import { PlatformModulesEnum } from '@thinknet-gmbh/platform-common';
import {
  from,
  split,
  ApolloClient,
  InMemoryCache,
  ApolloProvider as OriginApolloProvider,
} from '@apollo/client';

import { useSessionContext } from 'src/contexts';
import { gqlErrorHandler } from 'src/common/helpers/errors.helpers';
import { Session } from 'src/contexts/session.context/session.context.model';

export default function ApolloProvider(props: any) {
  const [session] = useSessionContext();
  const { accessToken, currentCompanyId }: Session = session;
  const tokenRef: React.MutableRefObject<null | string> = useRef(null);

  tokenRef.current = accessToken;

  const client = useMemo(() => {
    const authHeaders = {
      authorization: accessToken ? `Bearer ${accessToken}` : '',
      'platform-module': PlatformModulesEnum.YUU_HR_MODULE,
      'current-company-id': currentCompanyId || '',
    };

    const errorLink = onError(({ graphQLErrors }) => {
      if (graphQLErrors) {
        return gqlErrorHandler(graphQLErrors);
      }
    });

    const moduleLink = setContext((_, { headers }) => ({
      headers: { ...headers, ...authHeaders },
    }));

    const moduleHttpLink = createUploadLink({
      uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
    });

    const wsLink = new WebSocketLink({
      uri: `${process.env.REACT_APP_BACKEND_URL.replace(/^http?/, 'ws')}/subscriptions`,
      options: { reconnect: true, connectionParams: authHeaders },
    });
    const splitLink = split(
      ({ query }) => {
        const { kind, operation }: any = getMainDefinition(query);

        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink,
      moduleHttpLink
    );

    const link = from([errorLink as any, splitLink]);

    return new ApolloClient({
      link: moduleLink.concat(link),
      cache: new InMemoryCache({ addTypename: false }),
      defaultOptions: {
        mutate: { errorPolicy: 'ignore' },
      },
    });
  }, [accessToken, currentCompanyId]);

  return <OriginApolloProvider client={client} {...props} />;
}
